import Joi from "joi";
import { FieldErrors } from "react-hook-form";
import { shownFieldError } from "../../../utils/validationPatterns";
import { CustomRegistrationFormData, ProcessDataConfig } from "./types";

type FieldName =
  | "user_first_name"
  | "user_last_name"
  | "user_user_name"
  | "user_phone"
  | "user_country_code"
  | "user_birth_date";

const isFieldRequired = (
  config: ProcessDataConfig,
  fieldName: FieldName
): boolean => {
  if (config.fields?.[fieldName]) {
    return config.fields[fieldName].required;
  }
  return false;
};

const isFieldVisible = (
  config: ProcessDataConfig,
  fieldName: FieldName
): boolean => {
  if (config.fields?.[fieldName]) {
    return config.fields[fieldName].visible;
  }
  return false;
};

const textMessages = (required: string, length: string) => ({
  "any.required": required,
  "string.empty": required,
  "*": length,
});

const ANSWER_MESSAGES = {
  "string.max": "validation.answerTooLong",
  "*": "validation.answerRequired",
};

/** The schema's messages are clientView keys, translated when the steps show them. */
export const shownFieldErrors = (
  t: (key: string) => unknown,
  errors: FieldErrors<CustomRegistrationFormData>
) =>
  Object.fromEntries(
    Object.entries(errors).map(([name, error]) => [
      name,
      error && {
        ...error,
        message: shownFieldError(t, error.message as string | undefined),
      },
    ])
  ) as FieldErrors<CustomRegistrationFormData>;

export const buildValidationSchema = (config?: ProcessDataConfig) => {
  if (!config) return Joi.object({});

  const schemaFields: Record<string, Joi.Schema> = {
    user_email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages(
        textMessages("validation.emailRequired", "validation.emailInvalid")
      ),
  };

  const fieldDefinitions: Array<{
    name: FieldName;
    schema: Joi.StringSchema | Joi.DateSchema;
  }> = [
    {
      name: "user_first_name",
      schema: Joi.string()
        .min(2)
        .max(50)
        .messages(
          textMessages(
            "validation.firstNameRequired",
            "validation.firstNameLength"
          )
        ),
    },
    {
      name: "user_last_name",
      schema: Joi.string()
        .min(2)
        .max(50)
        .messages(
          textMessages(
            "validation.lastNameRequired",
            "validation.lastNameLength"
          )
        ),
    },
    {
      name: "user_user_name",
      schema: Joi.string()
        .min(3)
        .max(30)
        .messages(
          textMessages(
            "validation.usernameRequired",
            "validation.usernameLength"
          )
        ),
    },
    {
      name: "user_phone",
      schema: Joi.string()
        .min(8)
        .max(20)
        .messages(
          textMessages("validation.phoneRequired", "validation.phoneLength")
        ),
    },
    {
      name: "user_country_code",
      schema: Joi.string().messages({ "*": "validation.countryRequired" }),
    },
    {
      name: "user_birth_date",
      schema: Joi.date()
        .max("now")
        .min(new Date(new Date().getFullYear() - 120, 0, 1))
        .messages({
          "date.max": "validation.birthDateFuture",
          "*": "validation.birthDateInvalid",
        }),
    },
  ];

  fieldDefinitions.forEach(({ name, schema }) => {
    const visible = isFieldVisible(config, name);
    const required = isFieldRequired(config, name);

    if (visible && required) {
      schemaFields[name] = schema.required();
    } else if (visible && !required) {
      schemaFields[name] = schema.optional().allow("");
    }
  });

  config.customQuestions?.forEach((question, index) => {
    const questionKey = `question_${index}`;
    const isRequired = question.required !== false;

    if (question.fieldType === "file") {
      const fileSchema = Joi.object()
        .instance(File)
        .messages({ "*": "validation.fileRequired" });

      if (isRequired) {
        schemaFields[questionKey] = fileSchema.required();
      } else {
        schemaFields[questionKey] = fileSchema.optional().allow(null, "");
      }
    } else {
      if (isRequired) {
        schemaFields[questionKey] = Joi.string()
          .trim()
          .min(1)
          .max(1000)
          .required()
          .messages(ANSWER_MESSAGES);
      } else {
        schemaFields[questionKey] = Joi.string()
          .min(1)
          .max(1000)
          .optional()
          .allow("", null)
          .messages(ANSWER_MESSAGES);
      }
    }
  });

  return Joi.object(schemaFields);
};
